import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Truck, CheckCircle, Clock, AlertCircle, DollarSign,
  Receipt, ArrowRight, Search, MapPin, Calendar, Weight,
  TrendingUp, Bell, FileText, CreditCard, ChevronRight
} from 'lucide-react';

// Mock data for the dashboard
const statusCards = [
  { 
    id: 'in-transit', 
    label: 'In Transit', 
    value: '5', 
    icon: Truck, 
    color: 'blue',
    description: 'Packages on the way'
  },
  { 
    id: 'ready', 
    label: 'Ready for Pickup', 
    value: '3', 
    icon: CheckCircle, 
    color: 'lime',
    description: 'Available at warehouse'
  },
  { 
    id: 'awaiting-payment', 
    label: 'Awaiting Payment', 
    value: '2', 
    icon: Clock, 
    color: 'amber',
    description: 'Pay to release'
  },
  { 
    id: 'open-invoices', 
    label: 'Open Invoices', 
    value: '4', 
    icon: Receipt, 
    color: 'rose',
    description: 'JMD 45,230 total'
  },
];

const activeShipments = [
  {
    id: 'PKG-45821',
    tracking: 'BN-45821-JM',
    status: 'in_transit',
    statusText: 'In Transit to Jamaica',
    origin: 'Miami, FL',
    destination: 'Kingston, JA',
    weight: '12.5 lbs',
    eta: 'Dec 18, 2025',
    lastUpdate: '2 hours ago',
    timeline: [
      { status: 'Received in Florida', completed: true, date: 'Dec 12' },
      { status: 'Packed in Bag', completed: true, date: 'Dec 13' },
      { status: 'Manifested', completed: true, date: 'Dec 14' },
      { status: 'In Transit', completed: true, date: 'Dec 15', current: true },
      { status: 'Arrived in Jamaica', completed: false },
      { status: 'Cleared Customs', completed: false },
      { status: 'Ready for Pickup', completed: false },
    ]
  },
  {
    id: 'PKG-45789',
    tracking: 'BN-45789-JM',
    status: 'ready',
    statusText: 'Ready for Pickup',
    origin: 'Miami, FL',
    destination: 'Kingston, JA',
    weight: '8.2 lbs',
    eta: 'Ready Now',
    lastUpdate: '30 min ago',
    timeline: [
      { status: 'Received in Florida', completed: true, date: 'Dec 8' },
      { status: 'Packed in Bag', completed: true, date: 'Dec 9' },
      { status: 'Manifested', completed: true, date: 'Dec 10' },
      { status: 'In Transit', completed: true, date: 'Dec 11' },
      { status: 'Arrived in Jamaica', completed: true, date: 'Dec 14' },
      { status: 'Cleared Customs', completed: true, date: 'Dec 15' },
      { status: 'Ready for Pickup', completed: true, date: 'Dec 15', current: true },
    ]
  },
  {
    id: 'PKG-45756',
    tracking: 'BN-45756-JM',
    status: 'awaiting_payment',
    statusText: 'Awaiting Payment',
    origin: 'Orlando, FL',
    destination: 'Kingston, JA',
    weight: '22.1 lbs',
    amount: 'JMD 8,450',
    lastUpdate: '1 day ago',
    timeline: [
      { status: 'Received in Florida', completed: true, date: 'Dec 6' },
      { status: 'Packed in Bag', completed: true, date: 'Dec 7' },
      { status: 'Manifested', completed: true, date: 'Dec 8' },
      { status: 'In Transit', completed: true, date: 'Dec 9' },
      { status: 'Arrived in Jamaica', completed: true, date: 'Dec 12' },
      { status: 'Awaiting Payment', completed: false, current: true },
      { status: 'Ready for Pickup', completed: false },
    ]
  },
];

const recentInvoices = [
  { id: 'INV-8521', date: 'Dec 15, 2025', amount: 12450, status: 'pending', items: 3 },
  { id: 'INV-8498', date: 'Dec 10, 2025', amount: 8920, status: 'paid', items: 2 },
  { id: 'INV-8476', date: 'Dec 5, 2025', amount: 15680, status: 'paid', items: 4 },
];

const notifications = [
  { id: 1, type: 'ready', message: 'Package BN-45789-JM is ready for pickup', time: '30 min ago' },
  { id: 2, type: 'payment', message: 'Payment reminder for INV-8521 (JMD 12,450)', time: '2 hours ago' },
  { id: 3, type: 'transit', message: 'Package BN-45821-JM has departed Miami', time: '5 hours ago' },
];

const accountStats = {
  totalShipped: 47,
  totalWeight: '312.5 lbs',
  totalSpend: 'JMD 245,000',
  memberSince: 'March 2024'
};

export default function CustomerDashboard() {
  const [selectedShipment, setSelectedShipment] = useState(activeShipments[0]);
  const [searchTracking, setSearchTracking] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_transit': return 'blue';
      case 'ready': return 'lime';
      case 'awaiting_payment': return 'amber';
      case 'pending': return 'amber';
      case 'paid': return 'lime';
      default: return 'gray';
    }
  };

  return (
    <div className="customer-dashboard" data-testid="customer-dashboard">
      {/* Status Summary Cards */}
      <div className="status-cards-grid">
        {statusCards.map((card) => (
          <div 
            key={card.id} 
            className={`status-card ${card.color}`}
            data-testid={`status-${card.id}`}
          >
            <div className="status-card-icon">
              <card.icon size={24} />
            </div>
            <div className="status-card-content">
              <span className="status-card-value">{card.value}</span>
              <span className="status-card-label">{card.label}</span>
              <span className="status-card-desc">{card.description}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Active Shipments Timeline */}
        <div className="shipments-section">
          <div className="section-header-row">
            <h2>Active Shipments</h2>
            <Link to="/customer/tracking" className="view-all-link" data-testid="view-all-shipments">
              View All <ChevronRight size={16} />
            </Link>
          </div>

          {/* Quick Track Search */}
          <div className="quick-track-box">
            <Search size={18} />
            <input 
              type="text"
              placeholder="Enter tracking number..."
              value={searchTracking}
              onChange={(e) => setSearchTracking(e.target.value)}
              data-testid="dashboard-track-input"
            />
            <button className="btn btn-primary btn-sm" data-testid="track-btn">
              Track
            </button>
          </div>

          {/* Shipment Cards */}
          <div className="shipment-cards">
            {activeShipments.map((shipment) => (
              <div 
                key={shipment.id}
                className={`shipment-card ${selectedShipment?.id === shipment.id ? 'selected' : ''}`}
                onClick={() => setSelectedShipment(shipment)}
                data-testid={`shipment-${shipment.id}`}
              >
                <div className="shipment-card-header">
                  <div className="tracking-info">
                    <span className="tracking-number">{shipment.tracking}</span>
                    <span className={`status-badge ${getStatusColor(shipment.status)}`}>
                      {shipment.statusText}
                    </span>
                  </div>
                  <span className="last-update">{shipment.lastUpdate}</span>
                </div>
                
                <div className="shipment-card-body">
                  <div className="shipment-route">
                    <div className="route-point">
                      <MapPin size={14} />
                      <span>{shipment.origin}</span>
                    </div>
                    <div className="route-arrow">
                      <ArrowRight size={14} />
                    </div>
                    <div className="route-point">
                      <MapPin size={14} />
                      <span>{shipment.destination}</span>
                    </div>
                  </div>
                  
                  <div className="shipment-details">
                    <div className="detail-item">
                      <Weight size={14} />
                      <span>{shipment.weight}</span>
                    </div>
                    {shipment.eta && (
                      <div className="detail-item">
                        <Calendar size={14} />
                        <span>ETA: {shipment.eta}</span>
                      </div>
                    )}
                    {shipment.amount && (
                      <div className="detail-item amount">
                        <DollarSign size={14} />
                        <span>{shipment.amount}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline Preview */}
                <div className="shipment-timeline-preview">
                  {shipment.timeline.map((step, index) => (
                    <div 
                      key={index}
                      className={`timeline-dot ${step.completed ? 'completed' : ''} ${step.current ? 'current' : ''}`}
                      title={step.status}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel */}
        <div className="dashboard-right-panel">
          {/* Balance & Payment Section */}
          <div className="balance-card">
            <div className="balance-header">
              <h3>Outstanding Balance</h3>
              <Link to="/customer/payments" className="pay-now-link" data-testid="pay-now-btn">
                Pay Now <ArrowRight size={14} />
              </Link>
            </div>
            <div className="balance-amount">
              <DollarSign size={28} />
              <span>JMD 45,230</span>
            </div>
            <div className="balance-breakdown">
              <div className="breakdown-item">
                <span>Pending Invoices</span>
                <span>JMD 32,780</span>
              </div>
              <div className="breakdown-item">
                <span>Ready for Pickup</span>
                <span>JMD 12,450</span>
              </div>
            </div>
            <button className="btn btn-primary full-width" data-testid="make-payment-btn">
              <CreditCard size={16} /> Make Payment
            </button>
          </div>

          {/* Recent Invoices */}
          <div className="recent-invoices-card">
            <div className="card-header-row">
              <h3>Recent Invoices</h3>
              <Link to="/customer/invoices" className="view-all-link" data-testid="view-all-invoices">
                View All
              </Link>
            </div>
            <div className="invoices-list">
              {recentInvoices.map((invoice) => (
                <div key={invoice.id} className="invoice-item" data-testid={`invoice-${invoice.id}`}>
                  <div className="invoice-info">
                    <span className="invoice-id">{invoice.id}</span>
                    <span className="invoice-date">{invoice.date}</span>
                  </div>
                  <div className="invoice-right">
                    <span className="invoice-amount">JMD {invoice.amount.toLocaleString()}</span>
                    <span className={`status-dot ${invoice.status}`}></span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div className="notifications-card">
            <div className="card-header-row">
              <h3>Recent Updates</h3>
              <Link to="/customer/notifications" className="view-all-link" data-testid="view-all-notifications">
                View All
              </Link>
            </div>
            <div className="notifications-list">
              {notifications.map((notif) => (
                <div key={notif.id} className="notification-item" data-testid={`notif-${notif.id}`}>
                  <div className={`notif-icon ${notif.type}`}>
                    {notif.type === 'ready' && <CheckCircle size={14} />}
                    {notif.type === 'payment' && <AlertCircle size={14} />}
                    {notif.type === 'transit' && <Truck size={14} />}
                  </div>
                  <div className="notif-content">
                    <p>{notif.message}</p>
                    <span className="notif-time">{notif.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Account Stats */}
          <div className="account-stats-card">
            <h3>Your Shipping Stats</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <Package size={18} />
                <div>
                  <span className="stat-value">{accountStats.totalShipped}</span>
                  <span className="stat-label">Packages</span>
                </div>
              </div>
              <div className="stat-item">
                <Weight size={18} />
                <div>
                  <span className="stat-value">{accountStats.totalWeight}</span>
                  <span className="stat-label">Total Weight</span>
                </div>
              </div>
              <div className="stat-item">
                <TrendingUp size={18} />
                <div>
                  <span className="stat-value">{accountStats.totalSpend}</span>
                  <span className="stat-label">Total Spend</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Timeline for Selected Shipment */}
      {selectedShipment && (
        <div className="detailed-timeline-section" data-testid="detailed-timeline">
          <div className="timeline-header">
            <div>
              <h3>Shipment Timeline</h3>
              <p className="timeline-tracking">Tracking: {selectedShipment.tracking}</p>
            </div>
            <Link 
              to={`/customer/tracking?id=${selectedShipment.tracking}`} 
              className="btn btn-secondary"
              data-testid="view-full-details"
            >
              View Full Details <ArrowRight size={14} />
            </Link>
          </div>
          
          <div className="full-timeline">
            {selectedShipment.timeline.map((step, index) => (
              <div 
                key={index} 
                className={`timeline-step ${step.completed ? 'completed' : ''} ${step.current ? 'current' : ''}`}
              >
                <div className="timeline-marker">
                  {step.completed ? <CheckCircle size={16} /> : <div className="marker-dot" />}
                </div>
                <div className="timeline-content">
                  <span className="step-status">{step.status}</span>
                  {step.date && <span className="step-date">{step.date}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
